import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { ScheduleGridResponse, ScheduleQueryOptions } from '../lib/types';

/**
 * The cache key for one schedule window. Exported so a prefetch can be guaranteed to land on
 * the exact key the hook will later read — building it by hand at the call site is how a
 * prefetch silently misses.
 */
export function scheduleQueryKey(options: ScheduleQueryOptions) {
  return ['schedule', options] as const;
}

export function fetchSchedule(options: ScheduleQueryOptions): Promise<ScheduleGridResponse> {
  const params = new URLSearchParams();
  params.set('from', options.from);
  params.set('to', options.to);
  if (options.region) params.set('region', options.region);
  if (options.office) params.set('office', options.office);
  if (options.serviceLine) params.set('serviceLine', options.serviceLine);
  if (options.personId != null) params.set('personId', String(options.personId));
  for (const id of options.personIds ?? []) params.append('personIds', String(id));
  for (const id of options.projectIds ?? []) params.append('projectIds', String(id));

  return apiClient.get<ScheduleGridResponse>(`/api/schedule?${params}`);
}

export function useSchedule(options: ScheduleQueryOptions) {
  return useQuery({
    queryKey: scheduleQueryKey(options),
    queryFn: () => fetchSchedule(options),
    enabled: !!options.from && !!options.to,
  });
}
